import root from 'window-or-global';

import { AnalyticsIdentifiers, SessionProps } from 'components/Analytics/google-analytics-store';
import env from 'env/env';
import { getCookie } from 'utils/cookie';
import { GtmGenericEvent, GtmPurchaseEvent } from './convertGtmEventToGa4EventParams';

type WindowWithContentsquare = Window & {
  _uxa?: unknown[];
  CS_CONF?: unknown;
  dataLayer?: unknown;
  contentsquareFromDataLayer?: {
    handleDataLayerEvent: (event: GtmGenericEvent, sessionProps: SessionProps) => void;
  };
};

const UXA_KEY = '_uxa';

// Keep this in sync with convertGtmEventToGa4EventParams switch cases.
const SUPPORTED_EVENTS = new Set([
  'virtual_page_view',
  // 'monetate_fe_experiment_page_load',
  // 'monetate_fe_experiment_viewed',
  // 'checkout_review_order',
  // 'web_vitals',
  'modal_opened',
  'modal_closed',
  // 'notification_opened',
  // 'notification_clicked',
  // 'notification_closed',
  // 'remove_voucher_from_order',
  // 'checkout_click_confirm',
  // 'redeem_gift_card',
  // 'redeem_gift_voucher',
  // 'redeem_promo_code',
  // 'promo_code_added',
  // 'promo_code_removed',
  // 'promo_code_api_error',
  'transaction_completed',
]);

const win = root as unknown as WindowWithContentsquare;

function ensureUxaQueue(): unknown[] {
  win[UXA_KEY] = win[UXA_KEY] || [];
  return win[UXA_KEY];
}

function uxaPush(payload: unknown[]): void {
  ensureUxaQueue().push(payload);
}

function getLocationPath() {
  return window.location.pathname || '/';
}

function getLocationHash() {
  return (window.location.hash || '').replace(/^#/, '');
}

function getPageViewPath(path: string, hash?: string) {
  return hash ? `${path}?__${hash}` : path;
}

function setCustomVariable(value: string | undefined, slot: string, key: string) {
  if (value) {
    uxaPush(['setCustomVariable', slot, key, String(value), 3]);
  }
}

function trackDynamicVariable(key: string, value: string): void {
  uxaPush(['trackDynamicVariable', { key, value }]);
}

function trackPageView(path: string): void {
  uxaPush(['trackPageview', path]);
}

function handleArtificialPageview(modalName?: string): void {
  const path = getLocationPath();
  const hash = getLocationHash();

  if (hash !== '') {
    if (!modalName) {
      trackPageView(getPageViewPath(path, hash));
    } else {
      trackPageView(getPageViewPath(path));
    }
    return;
  }

  if (!modalName) {
    trackPageView(getPageViewPath(path));
    return;
  }

  trackPageView(`${path}?cs_query=${encodeURIComponent(modalName)}`);
}

function isSupportedEvent(event: unknown): event is string {
  return typeof event === 'string' && SUPPORTED_EVENTS.has(event);
}

function getShoppingMission(event: GtmPurchaseEvent): string {
  const products = event.ecommerce?.purchase?.products ?? [];

  const cats: Record<string, boolean> = {};

  const productCategories = products.map(p => {
    return p.category?.[1];
  });

  const productCategories2 = products.map(p => {
    return p.category?.[2];
  });

  productCategories.filter(Boolean).forEach(c => {
    cats[c] = true;
  });

  const catCount = productCategories.filter(p => {
    return p && p.match('Fresh & Chilled|Bakery');
  }).length;

  const treatCount = productCategories2
    .filter(p => {
      return p && p.match('Cakes & Tarts|Ice Cream, Frozen Yogurt & Sorbets|Crisps, Snacks & Nuts');
    })
    .concat(
      productCategories.filter(p => {
        return p && p.match('Beer, Wines & Spirits');
      }),
    ).length;

  const giftsCount = productCategories2
    .filter(p => {
      return (
        p && p.match('Crisps, Snacks & Nuts|Fresh Flowers & Plants|Cards, Gifts & Party|Gifts')
      );
    })
    .concat(
      productCategories.filter(p => {
        return p && p.match('Beer, Wines & Spirits');
      }),
    ).length;

  const staplesCount = productCategories2
    .filter(p => {
      return (
        p && p.match('Crisps, Snacks & Nuts|Fresh Flowers & Plants|Cards, Gifts & Party|Gifts')
      );
    })
    .concat(
      productCategories.filter(p => {
        return p && p.match('Beer, Wines & Spirits');
      }),
    ).length;

  if (products.length > 29 && Object.keys(cats).length > 10) {
    return 'Full Shop';
  }

  if (products.length < 11 && treatCount / products.length > 0.74) {
    return 'Everyday Treat';
  }

  if (products.length < 11 && giftsCount / products.length > 0.74) {
    return 'Gifts';
  }

  if (products.length < 11 && catCount / products.length > 0.59) {
    return 'Staples Restock';
  }

  if (products.length < 36 && staplesCount / products.length > 0.49) {
    return 'Staples Restock';
  }

  if (products.length > 1 && products.length < 36 && catCount / products.length > 0.39) {
    return 'Fresh Top Up';
  }

  return 'Other';
}

type GtmVirtualPageViewEvent = {
  event: 'virtual_page_view';
  page: {
    title: string;
    type: string;
  };
};

function loadMainScriptOnce() {
  if (window.CS_CONF || !window._uxa?.contentsquareScript) {
    return false;
  }

  const path = getLocationPath();
  const hash = getLocationHash();
  uxaPush(['setPath', getPageViewPath(path, hash)]);
  if (env.name !== 'prod') {
    window._uxa.push(['replay:resourceManager:enableForOnlineResource:nextPageviewOnly']);
  }

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = window._uxa.contentsquareScript as string;
  document.head.appendChild(script);

  return true;
}

function trackMainPageviewAndCommonVars(
  event: GtmVirtualPageViewEvent,
  sessionProps: SessionProps,
): void {
  // Checkout only is interested in slots 1, 2, 7, 17 & 18.
  // The rest are commented out for reference from the full list in the GTM Contentsquare tag.
  const cvars: [string, string, string | undefined][] = [
    ['1', 'Page Type', event.page?.type],
    ['2', 'Page Name', event.page?.title],
    // ['3', 'Product Brand', dl?.ecommerce?.detail?.products?.[0]?.brand],
    // ['4', 'Product Category 1', dl?.ecommerce?.detail?.products?.[0]?.category?.[0]],
    // ['5', 'Product Category 2', dl?.ecommerce?.detail?.products?.[0]?.category?.[1]],
    // ['6', 'Product Name', dl?.ecommerce?.detail?.products?.[0]?.name],
    ['7', 'User-logged', 'true'],
    // ['9', 'Delivery/Click and collect', sessionProps.service_type],
    // ['11', 'Search Page', dl?.search_number_of_results],
    // ['13', 'Page Category 1', dl?.page?.category_level_1],
    // ['14', 'Page Category 2', dl?.page?.category_level_2],
    // ['15', 'Page Category 3', dl?.page?.category_level_3],
    // ['16', 'Page Category 4', dl?.page?.category_level_4],
    ['17', 'Branch ID', sessionProps.branch_id],
    ['18', 'Page Service', sessionProps.service_type],
  ];

  cvars.forEach(([slot, key, value]) => {
    setCustomVariable(value, slot, key);
  });

  if (!loadMainScriptOnce()) {
    handleArtificialPageview();
  }
}

export function handleDataLayerEvent(event: GtmGenericEvent, sessionProps: SessionProps): void {
  if (!event || typeof event !== 'object' || !isSupportedEvent(event.event)) {
    return;
  }

  switch (event.event) {
    case 'virtual_page_view': {
      trackMainPageviewAndCommonVars(event as GtmVirtualPageViewEvent, sessionProps);
      trackDynamicVariable(
        'Loyalty Member Status',
        sessionProps.mywaitrose_member ? 'member' : 'non member',
      );
      return;
    }

    case 'modal_opened': {
      const modalName =
        (event.modal as { id?: string } | undefined)?.id ||
        (event.modal_name as string | undefined) ||
        undefined;
      handleArtificialPageview(modalName);
      return;
    }

    case 'modal_closed': {
      handleArtificialPageview(undefined);
      return;
    }

    case 'transaction_completed': {
      const purchaseEvent = event as unknown as GtmPurchaseEvent;

      const actionField = purchaseEvent.ecommerce?.purchase?.actionField;
      // only track the initial order purchase and not any subsequent amendments
      const option = actionField?.option;
      if (option === 'purchase') {
        uxaPush([
          'ec:transaction:create',
          {
            id: actionField?.id,
            revenue: actionField?.revenue,
            currency: 'GBP',
          },
        ]);
        uxaPush(['ec:transaction:send']);
      }
      trackDynamicVariable('Shopping Mission', getShoppingMission(purchaseEvent));
      break;
    }

    default:
    // no-op as no other events in supported list
  }
}

export function initialiseContentsquareAdapter({
  clientId,
  sessionId,
}: AnalyticsIdentifiers): void {
  if (__SERVER__) {
    return;
  }
  const params = new URLSearchParams(window.location.search);
  const utmMedium = params.get('utm_medium') || '';
  const utmSource = params.get('utm_source') || '';
  const utmCampaign = params.get('utm_campaign') || '';
  const gclid = params.get('gclid') || '';

  if (utmMedium !== '') {
    trackDynamicVariable('Medium', utmMedium.toLowerCase());
  }

  if (utmSource !== '') {
    trackDynamicVariable('Source', utmSource.toLowerCase());
  }

  if (utmCampaign !== '') {
    trackDynamicVariable('Campaign', utmCampaign.toLowerCase());
  }

  if (gclid !== '') {
    trackDynamicVariable('Gclid', 'true');
  }

  if (sessionId) {
    trackDynamicVariable('ga_session_id', sessionId);
  }
  if (clientId) {
    trackDynamicVariable('AN_GA_ClientID', clientId);
  }

  const wtrUuid = getCookie('wtr_uuid');
  if (wtrUuid) {
    trackDynamicVariable('wtr_uuid', wtrUuid);
  }
}
